import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMyBookings, updatePreferences } from '../api';

const categories = ['conference', 'workshop', 'concert', 'sports', 'networking'];

function Profile() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('bookings');
  const [bookings, setBookings] = useState([]);
  const [preferences, setPreferences] = useState({ categories: [], notifications: true });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadBookings();
    if (user.preferences) {
      setPreferences(user.preferences);
    }
  }, [user, navigate]);

  const loadBookings = async () => {
    try {
      const { data } = await getMyBookings();
      setBookings(data);
    } catch (err) {
      console.error('Error loading bookings:', err);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(price);
  };

  const handleSavePreferences = async () => {
    try {
      await updatePreferences(preferences);
      await refreshUser();
      alert('Preferences saved!');
    } catch (err) {
      alert('Error saving preferences');
    }
  };

  const toggleCategory = (cat) => {
    const cats = preferences.categories || [];
    const updated = cats.includes(cat) 
      ? cats.filter(c => c !== cat)
      : [...cats, cat];
    setPreferences({ ...preferences, categories: updated });
  };

  if (!user) return null;

  const totalSpent = bookings.reduce((sum, b) => sum + b.totalAmount, 0);
  const totalTickets = bookings.reduce((sum, b) => sum + b.quantity, 0);

  return (
    <div>
      <div className="profile-header">
        <div className="profile-avatar">👤</div>
        <div className="profile-info">
          <h2>{user.name}</h2>
          <p>{user.email}</p>
        </div>
        <div className="profile-stats">
          <div className="stat">
            <span className="stat-value">{bookings.length}</span>
            <span className="stat-label">Bookings</span>
          </div>
          <div className="stat">
            <span className="stat-value">{totalTickets}</span>
            <span className="stat-label">Tickets</span>
          </div>
          <div className="stat">
            <span className="stat-value">{formatPrice(totalSpent)}</span>
            <span className="stat-label">Total Spent</span>
          </div>
        </div>
      </div>

      <div className="tabs">
        <button className={`tab ${tab === 'bookings' ? 'active' : ''}`} onClick={() => setTab('bookings')}>
          My Bookings
        </button>
        <button className={`tab ${tab === 'preferences' ? 'active' : ''}`} onClick={() => setTab('preferences')}>
          Preferences
        </button>
      </div>

      {tab === 'bookings' && (
        <div>
          <h2>Booking History</h2>
          {bookings.length > 0 ? (
            <div className="bookings-list">
              {bookings.map(booking => (
                <div key={booking._id} className="booking-card">
                  <div className="booking-event">
                    <h3>{booking.event?.title || 'Event'}</h3>
                    <p>📅 {new Date(booking.event?.date).toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</p>
                    <p>📍 {booking.event?.location}</p>
                  </div>
                  <div className="booking-details">
                    <div className="booking-info">
                      <span className="label">Tickets</span>
                      <span className="value">{booking.quantity}</span>
                    </div>
                    <div className="booking-info">
                      <span className="label">Price/Ticket</span>
                      <span className="value">{formatPrice(booking.pricePerTicket)}</span>
                    </div>
                    <div className="booking-info total">
                      <span className="label">Total</span>
                      <span className="value">{formatPrice(booking.totalAmount)}</span>
                    </div>
                  </div>
                  <div className="booking-meta">
                    <span className={`status ${booking.status}`}>{booking.status}</span>
                    <span className="txn-id">TXN: {booking.transactionId}</span>
                    <span className="date">Booked on {new Date(booking.createdAt).toLocaleDateString('en-IN')}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <span>🎫</span>
              <h3>No bookings yet</h3>
              <p>Browse events and book tickets for ones you like!</p>
              <Link to="/" className="btn btn-primary" style={{ marginTop: '20px' }}>Browse Events</Link>
            </div>
          )}
        </div>
      )}

      {tab === 'preferences' && (
        <div>
          <h2>Event Preferences</h2>
          <div className="card">
            <div className="form-group">
              <label>Preferred Event Categories</label>
              <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '15px' }}>
                Select categories you're interested in
              </p>
              <div className="categories">
                {categories.map(cat => (
                  <button
                    key={cat}
                    type="button"
                    className={`category-btn ${preferences.categories?.includes(cat) ? 'active' : ''}`}
                    onClick={() => toggleCategory(cat)}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group" style={{ marginTop: '25px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={preferences.notifications}
                  onChange={(e) => setPreferences({ ...preferences, notifications: e.target.checked })}
                  style={{ width: '20px', height: '20px' }}
                />
                Receive email notifications about new events
              </label>
            </div>

            <button onClick={handleSavePreferences} className="btn btn-primary" style={{ marginTop: '25px' }}>
              Save Preferences
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;
